import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star, ThumbsUp, ThumbsDown, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';

type Scale = 'stars_5' | 'stars_10' | 'nps' | 'thumbs' | 'emoji';

interface Question {
  id: string;
  text: string;
  scale: Scale | string;
  required: boolean;
  lowLabel?: string;
  highLabel?: string;
}

interface Branding {
  logoUrl?: string;
  primaryColor?: string;
  backgroundColor?: string;
  thankYouMessage?: string;
}

interface FollowUp {
  enabled: boolean;
  questionText?: string;
  showBelowRating?: number;
}

interface RenderPayload {
  id: string;
  status: string;
  expiresAt: string;
  respondedAt: string | null;
  questions: Question[];
  branding: Branding;
  followUp: FollowUp;
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'expired' }
  | { kind: 'completed'; thankYou?: string }
  | { kind: 'ready'; data: RenderPayload }
  | { kind: 'submitting'; data: RenderPayload }
  | { kind: 'submitted'; thankYou?: string };

const EMOJI_FACES = ['😞', '😕', '😐', '🙂', '😊'];

/** Page background when the tenant hasn't set one. Kept in step with the
 *  fallback the CSAT settings preview uses, so the preview doesn't lie. */
export const DEFAULT_PAGE_BG = '#F9FAFB';
export const DEFAULT_PRIMARY = '#3B82F6';

/**
 * Coerce a branding colour to a 6-digit hex, or fall back.
 *
 * The settings page pairs a colour picker (always `#rrggbb`) with a free-text
 * field that accepted anything, and nothing validated it on the way to the
 * database — so `red`, `#F00` or an 8-digit `#rrggbbaa` can all be stored. That
 * matters here because the tint helpers below append two hex digits of alpha to
 * this string: `red` becomes `red50`, `#F00` becomes `#F0050`, both invalid, and
 * the browser silently drops the declaration so the tint just disappears.
 * Expanding 3-digit shorthand and rejecting the rest keeps the tints working
 * whatever is in the column.
 */
export function toHex6(value: string | undefined, fallback: string): string {
  const raw = value?.trim() ?? '';
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
  }
  return fallback;
}

/** `#rrggbb` + two hex digits of alpha. Safe because the input is normalised. */
export function withAlpha(hex6: string, alphaHex: string): string {
  return `${hex6}${alphaHex}`;
}

export function FeedbackPage() {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<ViewState>({ kind: 'loading' });
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [followUpText, setFollowUpText] = useState('');
  const [generalFeedback, setGeneralFeedback] = useState('');
  // Branding is held separately from `view` on purpose. It used to be read off
  // `view.data`, which only exists while the survey is being filled in — so the
  // moment the customer submitted, the logo vanished and the page snapped from
  // the tenant's background colour back to the default grey. Same on the
  // "already responded" screen. The thank-you text survived (it rides along on
  // the view state), which is what made it look like branding was working.
  const [branding, setBranding] = useState<Branding>({});
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    if (!token) {
      setView({ kind: 'error', message: 'Invalid survey link.' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/csat/feedback/${encodeURIComponent(token)}`);
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.message || `Survey not found (HTTP ${res.status})`);
        }
        const data: RenderPayload = await res.json();
        if (cancelled) return;
        // Stash branding before narrowing to a view state, so every screen —
        // including the ones that carry no survey data — stays branded.
        setBranding(data.branding ?? {});
        if (data.status === 'completed') {
          setView({ kind: 'completed', thankYou: data.branding.thankYouMessage });
        } else if (data.status === 'expired') {
          setView({ kind: 'expired' });
        } else {
          setView({ kind: 'ready', data });
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load survey';
        setView({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const data = view.kind === 'ready' || view.kind === 'submitting' ? view.data : null;

  const primaryRating = useMemo(() => {
    if (!data || data.questions.length === 0) return undefined;
    return answers[data.questions[0].id];
  }, [answers, data]);

  const showFollowUp = useMemo(() => {
    if (!data || !data.followUp.enabled) return false;
    if (primaryRating === undefined) return false;
    const threshold = data.followUp.showBelowRating ?? 3;
    return primaryRating <= threshold;
  }, [data, primaryRating]);

  const canSubmit = useMemo(() => {
    if (!data || data.questions.length === 0) return false;
    // The first question supplies the primary `rating` the submission is built
    // around, so it has to be answered even when the admin unticked Required on
    // it. Without this, Submit enabled, the page posted rating 0, and the
    // backend rejected it (@Min(1)) — the customer got a button that could
    // never succeed, with no indication why.
    if (answers[data.questions[0].id] === undefined) return false;
    return data.questions.every((q) => !q.required || answers[q.id] !== undefined);
  }, [data, answers]);

  const handleSubmit = async () => {
    if (!data || !token || !canSubmit) return;
    setView({ kind: 'submitting', data });
    try {
      const responses = data.questions
        .filter((q) => answers[q.id] !== undefined)
        .map((q) => ({ questionId: q.id, rating: answers[q.id] }));

      const body = {
        rating: primaryRating ?? 0,
        feedback: generalFeedback.trim() || undefined,
        followUpFeedback: showFollowUp && followUpText.trim() ? followUpText.trim() : undefined,
        responses,
      };

      const res = await fetch(`/api/csat/feedback/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.message || `Submission failed (HTTP ${res.status})`);
      }
      setView({ kind: 'submitted', thankYou: data.branding.thankYouMessage });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit';
      setView({ kind: 'error', message });
    }
  };

  // Read from `branding`, not `data` — see the state declaration above.
  const bgColor = branding.backgroundColor?.trim() || DEFAULT_PAGE_BG;
  const primaryColor = toHex6(branding.primaryColor, DEFAULT_PRIMARY);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: bgColor }}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg p-8 sm:p-10">
        {/* onError hides a logo whose URL is broken or blocked (a http:// logo on
            an https portal is mixed content), rather than leaving the customer
            looking at a broken-image icon above their survey. */}
        {branding.logoUrl && !logoFailed && (
          <div className="flex justify-center mb-6">
            <img
              src={branding.logoUrl}
              alt="Logo"
              className="h-12 max-w-[200px] object-contain"
              onError={() => setLogoFailed(true)}
            />
          </div>
        )}

        {view.kind === 'loading' && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin mb-3" />
            <p className="text-sm">Loading your survey…</p>
          </div>
        )}

        {view.kind === 'error' && (
          <ResultBanner
            icon={<AlertCircle className="h-12 w-12 text-red-500" />}
            title="We couldn't load your survey"
            body={view.message}
          />
        )}

        {view.kind === 'expired' && (
          <ResultBanner
            icon={<Clock className="h-12 w-12 text-gray-400" />}
            title="This survey has expired"
            body="Thanks anyway — we still appreciate your time."
          />
        )}

        {view.kind === 'completed' && (
          <ResultBanner
            icon={<CheckCircle2 className="h-12 w-12 text-green-500" />}
            title="You've already responded"
            body={view.thankYou || 'Thank you for your feedback! We appreciate your time.'}
          />
        )}

        {view.kind === 'submitted' && (
          <ResultBanner
            icon={<CheckCircle2 className="h-12 w-12 text-green-500" />}
            title="Thank you!"
            body={view.thankYou || 'Your feedback has been recorded. We appreciate your time.'}
          />
        )}

        {(view.kind === 'ready' || view.kind === 'submitting') && data && (
          <div className="space-y-8">
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-gray-900">Tell us about your experience</h1>
              <p className="text-sm text-gray-500 mt-2">Your feedback helps us improve.</p>
            </div>

            {data.questions.length === 0 ? (
              <p className="text-center text-gray-500 py-6">
                This survey has no questions configured. Please contact support.
              </p>
            ) : (
              data.questions.map((question, index) => (
                <QuestionBlock
                  key={question.id}
                  question={question}
                  questionNumber={index + 1}
                  totalQuestions={data.questions.length}
                  selected={answers[question.id]}
                  onSelect={(rating) => setAnswers({ ...answers, [question.id]: rating })}
                  primaryColor={primaryColor}
                  isPrimary={index === 0}
                />
              ))
            )}

            {showFollowUp && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {data.followUp.questionText || 'What could we have done better?'}
                </label>
                <textarea
                  value={followUpText}
                  onChange={(e) => setFollowUpText(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-offset-1"
                  style={{ borderColor: withAlpha(primaryColor, '50') }}
                  placeholder="Your honest feedback helps us improve…"
                />
                <p className="text-xs text-gray-400 mt-1">{followUpText.length}/2000</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anything else you'd like to share? <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={generalFeedback}
                onChange={(e) => setGeneralFeedback(e.target.value)}
                rows={3}
                maxLength={2000}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                placeholder="Optional comments…"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={!canSubmit || view.kind === 'submitting'}
              className="w-full py-3 rounded-lg text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              style={{ backgroundColor: primaryColor }}
            >
              {view.kind === 'submitting' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting…
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultBanner({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center text-center py-8">
      <div className="mb-4">{icon}</div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-sm text-gray-600 max-w-md">{body}</p>
    </div>
  );
}

function QuestionBlock({
  question,
  questionNumber,
  totalQuestions,
  selected,
  onSelect,
  primaryColor,
  isPrimary,
}: {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
  selected?: number;
  onSelect: (rating: number) => void;
  primaryColor: string;
  /** First question — supplies the primary rating, so Submit stays disabled
   *  until it's answered regardless of its `required` flag. Marked accordingly
   *  so the customer isn't staring at a dead button with no asterisk to explain
   *  it. */
  isPrimary?: boolean;
}) {
  return (
    <div>
      {totalQuestions > 1 && (
        <p className="text-xs text-gray-400 mb-1">
          Question {questionNumber} of {totalQuestions}
        </p>
      )}
      <p className="text-base font-medium text-gray-900 mb-4">
        {question.text}
        {(question.required || isPrimary) && <span className="text-red-500 ml-1">*</span>}
      </p>
      <ScaleWidget
        scale={question.scale as Scale}
        selected={selected}
        onSelect={onSelect}
        lowLabel={question.lowLabel}
        highLabel={question.highLabel}
        primaryColor={primaryColor}
      />
    </div>
  );
}

function ScaleWidget({
  scale,
  selected,
  onSelect,
  lowLabel,
  highLabel,
  primaryColor,
}: {
  scale: Scale;
  selected?: number;
  onSelect: (rating: number) => void;
  lowLabel?: string;
  highLabel?: string;
  primaryColor: string;
}) {
  switch (scale) {
    // Both star scales render here. stars_10 used to fall through to the NPS
    // branch below, so an admin who picked "10-Star Rating" showed the customer
    // a numbered 1-10 row identical to NPS — no stars at all.
    case 'stars_5':
    case 'stars_10': {
      const starCount = scale === 'stars_10' ? 10 : 5;
      return (
        <ScaleWithLabels lowLabel={lowLabel} highLabel={highLabel}>
          {/* Ten h-10 stars overflow a narrow phone, so the wider scale gets
              smaller icons and is allowed to wrap. */}
          <div className="flex justify-center gap-1 sm:gap-2 flex-wrap">
            {Array.from({ length: starCount }, (_, i) => i + 1).map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => onSelect(rating)}
                className="transition-transform hover:scale-110"
                aria-label={`${rating} of ${starCount} stars`}
              >
                <Star
                  className={`${
                    starCount === 10 ? 'h-8 w-8 sm:h-9 sm:w-9' : 'h-10 w-10'
                  } ${
                    selected !== undefined && selected >= rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </ScaleWithLabels>
      );
    }

    case 'thumbs':
      return (
        <ScaleWithLabels lowLabel={lowLabel} highLabel={highLabel}>
          <div className="flex justify-center gap-6">
            <button
              type="button"
              onClick={() => onSelect(1)}
              className={`p-4 rounded-full transition ${
                selected === 1 ? 'bg-red-100 text-red-600 ring-2 ring-red-300' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
              aria-label="Thumbs down"
            >
              <ThumbsDown className="h-8 w-8" />
            </button>
            <button
              type="button"
              onClick={() => onSelect(5)}
              className={`p-4 rounded-full transition ${
                selected === 5 ? 'bg-green-100 text-green-600 ring-2 ring-green-300' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
              aria-label="Thumbs up"
            >
              <ThumbsUp className="h-8 w-8" />
            </button>
          </div>
        </ScaleWithLabels>
      );

    case 'emoji':
      return (
        <ScaleWithLabels lowLabel={lowLabel} highLabel={highLabel}>
          <div className="flex justify-center gap-2">
            {EMOJI_FACES.map((emoji, index) => {
              const rating = index + 1;
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onSelect(rating)}
                  className={`text-4xl p-2 rounded-lg transition ${
                    selected === rating ? 'scale-125' : 'hover:scale-110 grayscale-[40%]'
                  }`}
                  style={selected === rating ? { backgroundColor: withAlpha(primaryColor, '20') } : undefined}
                  aria-label={`Rating ${rating}`}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        </ScaleWithLabels>
      );

    // NOTE: renders 1-10, but real NPS is 0-10 (and the settings dropdown says
    // so). Correcting it means allowing a rating of 0, which reaches the submit
    // validators in the BFF and ticket-service, the 1..10 ratingDistribution
    // loop, and the nps calculation itself (which currently uses 5-star
    // promoter/detractor bands) — tracked separately, not part of the stars_10 fix.
    case 'nps':
      return (
        <ScaleWithLabels lowLabel={lowLabel} highLabel={highLabel}>
          <div className="flex justify-center gap-1 sm:gap-2 flex-wrap">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((rating) => {
              const isSelected = selected === rating;
              return (
                <button
                  key={rating}
                  type="button"
                  onClick={() => onSelect(rating)}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded text-sm font-semibold transition border"
                  style={
                    isSelected
                      ? { backgroundColor: primaryColor, color: '#fff', borderColor: primaryColor }
                      : { backgroundColor: '#F3F4F6', color: '#4B5563', borderColor: '#E5E7EB' }
                  }
                  aria-label={`Rating ${rating}`}
                >
                  {rating}
                </button>
              );
            })}
          </div>
        </ScaleWithLabels>
      );

    default:
      return (
        <p className="text-sm text-gray-500 text-center">
          Unsupported scale: <code>{scale}</code>
        </p>
      );
  }
}

function ScaleWithLabels({
  lowLabel,
  highLabel,
  children,
}: {
  lowLabel?: string;
  highLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      {(lowLabel || highLabel) && (
        <div className="flex justify-between text-xs text-gray-500 mb-2 px-2">
          <span>{lowLabel || ''}</span>
          <span>{highLabel || ''}</span>
        </div>
      )}
      {children}
    </div>
  );
}

export default FeedbackPage;
